import { cookies } from '../../../assets/scripts/helpers/cookies';
import { COOKIE_LANDING_PAGE, COOKIE_REF, COOKIE_REF_DOMAIN, COOKIE_UTM, COOKIE_UTM_VISITS, UTM_KEYS, getAnalyticsUid, getStoredLastUtm, getVisits, isLegacyUtmCookie, normalizeUtm, parseUtmCookie, setStoredLastUtm, utmToVisit } from '../../../assets/scripts/helpers/utm-cookies';

// First touch kept by WP while `productive-utm` held the last touch, read once to repair `productive-utm` and then deleted.
// TODO: Remove after 1 November 2027, together with its branch in `trackFirstTouch`. These cookies have expired by then.
const COOKIE_UTM_FIRST = 'productive-utm-first';
const SESSION_VISIT_SIGNATURE = 'productive-visit-sig';
const MAX_VISITS = 10;
// Browsers reject cookies over 4096 bytes, and some count the attributes too, so leave headroom for them.
const MAX_COOKIE_SIZE = 4000;

/**
 * Write UTM, visits, referrer, landing page and analytics UID cookies, and the last touch to localStorage.
 *
 * Shared state is read up front, so no write depends on an earlier one.
 *
 * @returns {void}
 */
export function trackUtm() {
	const params = new URLSearchParams(window.location.search);
	const utm = normalizeUtm(Object.fromEntries(UTM_KEYS.map((key) => [key, params.get(`utm_${key}`) || ''])));
	const hasUtm = UTM_KEYS.some((key) => utm[key]);
	const refHost = getReferrerHost();
	const visits = getVisits();

	trackFirstTouch({ utm, hasUtm });

	if (hasUtm) {
		setStoredLastUtm(utm);
	}

	trackReferrer({ refParam: params.get('ref'), refHost });
	logVisit({ utm, hasUtm, refHost, visits });

	if (!cookies.getCookie(COOKIE_LANDING_PAGE)) {
		cookies.setCookie(COOKIE_LANDING_PAGE, window.location.pathname, cookies.DURATION_ONE_YEAR);
	}

	getAnalyticsUid();
}

/**
 * Write the first touch UTM, only when the cookie doesn't exist yet. The last touch is read from the visits cookie.
 *
 * Values written by older WP tracking are repaired once: the first touch from `productive-utm-first` replaces the
 * last touch WP kept in `productive-utm`, and the legacy raw JSON format is re-encoded in the shared format.
 * The replaced last touch moves to localStorage, unless a newer one is already there.
 *
 * @param {object} args First touch arguments.
 * @param {object} args.utm Current UTM values.
 * @param {boolean} args.hasUtm Whether the URL contains any UTM parameter.
 *
 * @returns {void}
 */
function trackFirstTouch({ utm, hasUtm }) {
	const utmValue = cookies.getCookie(COOKIE_UTM);
	const firstValue = cookies.getCookie(COOKIE_UTM_FIRST);

	// Repair: `productive-utm-first` holds the real first touch, `productive-utm` the last touch.
	if (firstValue !== null) {
		const firstUtm = parseUtmCookie(firstValue);

		cookies.setCookie(COOKIE_UTM_FIRST, '', -cookies.DURATION_ONE_YEAR);

		if (firstUtm) {
			const lastUtm = parseUtmCookie(utmValue);

			// A UTM in the URL or a last touch stored after the last WP visit is newer than the one being replaced.
			if (lastUtm && !hasUtm && !getStoredLastUtm()) {
				setStoredLastUtm(lastUtm);
			}

			setJsonCookie(COOKIE_UTM, normalizeUtm(firstUtm));

			return;
		}
	}

	// Repair: the legacy raw JSON cookie already holds the first touch, only the format changes.
	if (isLegacyUtmCookie(utmValue)) {
		const legacyUtm = parseUtmCookie(utmValue);

		if (legacyUtm) {
			setJsonCookie(COOKIE_UTM, normalizeUtm(legacyUtm));
		}

		return;
	}

	if (hasUtm && !utmValue) {
		setJsonCookie(COOKIE_UTM, utm);
	}
}

/**
 * Write the referrer cookies, each only when it doesn't exist yet.
 *
 * The `ref` URL parameter takes precedence over an external referrer for `productive-ref`.
 *
 * @param {object} args Referrer arguments.
 * @param {string|null} args.refParam Value of the `ref` URL parameter.
 * @param {string} args.refHost Referrer hostname.
 *
 * @returns {void}
 */
function trackReferrer({ refParam, refHost }) {
	const isExternalRef = refHost !== '' && !isInternalHost(refHost);

	if (!cookies.getCookie(COOKIE_REF)) {
		const ref = refParam || (isExternalRef ? document.referrer : '');

		if (ref) {
			cookies.setCookie(COOKIE_REF, encodeURIComponent(ref), cookies.DURATION_ONE_YEAR);
		}
	}

	if (isExternalRef && !cookies.getCookie(COOKIE_REF_DOMAIN)) {
		cookies.setCookie(COOKIE_REF_DOMAIN, refHost, cookies.DURATION_ONE_YEAR);
	}
}

/**
 * Append a visit, once per session entry or whenever a new UTM combination arrives.
 *
 * @param {object} args Visit arguments.
 * @param {object} args.utm Current UTM values.
 * @param {boolean} args.hasUtm Whether the URL contains any UTM parameter.
 * @param {string} args.refHost Referrer hostname.
 * @param {Array} args.visits Visits currently stored in the cookie.
 *
 * @returns {void}
 */
function logVisit({ utm, hasUtm, refHost, visits }) {
	const signature = hasUtm ? UTM_KEYS.map((key) => utm[key]).join('|') : 'none';
	let lastSignature = null;

	try {
		lastSignature = sessionStorage.getItem(SESSION_VISIT_SIGNATURE);
	} catch {
		// sessionStorage blocked by the browser.
	}

	const shouldLog = hasUtm ? signature !== lastSignature : lastSignature === null && !isInternalHost(refHost);

	if (!shouldLog) {
		return;
	}

	const updatedVisits = limitVisits([
		...visits,
		{
			...utmToVisit(utm),
			r: refHost,
			lp: window.location.pathname,
			ts: Date.now(),
		},
	]);

	// The new visit alone doesn't fit, keep the stored visits instead of wiping them.
	if (!updatedVisits.length) {
		return;
	}

	setJsonCookie(COOKIE_UTM_VISITS, updatedVisits);

	try {
		sessionStorage.setItem(SESSION_VISIT_SIGNATURE, signature);
	} catch {
		// sessionStorage blocked by the browser.
	}
}

/**
 * Keep the newest visits that fit both the visit count and the cookie size limit.
 *
 * An oversized cookie is silently rejected by the browser, so the oldest visits are dropped until it fits.
 *
 * @param {Array} visits Visits, oldest first.
 *
 * @returns {Array} Newest visits that fit the visits cookie, empty if not even the newest one fits.
 */
function limitVisits(visits) {
	const limitedVisits = visits.slice(-MAX_VISITS);

	while (limitedVisits.length && `${COOKIE_UTM_VISITS}=${encodeURIComponent(JSON.stringify(limitedVisits))}`.length > MAX_COOKIE_SIZE) {
		limitedVisits.shift();
	}

	return limitedVisits;
}

/**
 * Get the referrer hostname without `www.`, kept short so the visits cookie stays small.
 *
 * @returns {string} Referrer hostname or an empty string.
 */
function getReferrerHost() {
	try {
		return document.referrer ? stripWww(new URL(document.referrer).hostname) : '';
	} catch {
		return '';
	}
}

/**
 * Check if the hostname is productive.io, one of its subdomains, or the current host.
 *
 * @param {string} hostname Hostname to check, without `www.`.
 *
 * @returns {boolean} True if internal.
 */
function isInternalHost(hostname) {
	return /(^|\.)productive\.io$/.test(hostname) || hostname === stripWww(window.location.hostname);
}

/**
 * Remove the leading `www.` from a hostname.
 *
 * @param {string} hostname Hostname.
 *
 * @returns {string} Hostname without `www.`.
 */
function stripWww(hostname) {
	return hostname.replace(/^www\./, '');
}

/**
 * Set a URL-encoded JSON cookie.
 *
 * @param {string} name Cookie name.
 * @param {object|Array} value Cookie value.
 *
 * @returns {void}
 */
function setJsonCookie(name, value) {
	cookies.setCookie(name, encodeURIComponent(JSON.stringify(value)), cookies.DURATION_ONE_YEAR);
}
